/**
 * Flat, token-name-keyed view of every token.
 *
 * Keys are dotted token-name strings (`color.intent.primary`, `spacing.md`,
 * `typography.fontWeight.semibold`, …) and values are the resolved primitive
 * strings. This is the surface a later W3C Design Tokens / Style Dictionary
 * JSON export transforms mechanically — each entry becomes one
 * `{ "$value": ..., "$type": ... }` node with no consumer-specific shaping.
 *
 * It is derived from the structured `as const` objects so the two views cannot
 * drift; the structured objects remain the ergonomic, fully-typed API.
 */
import { chartCategorical, chartSequential } from './chart.js';
import { border, buttonColor, buttonDisabled, buttonFilledBackgroundState, intent, intentIndicator, intentTint, interactionState, onColor, surface, text, } from './color.js';
import { commonColors, primitiveRamps } from './primitives.js';
import { elevation, fontFamily, fontSize, fontWeight, headingSize, layer, lineHeight, radius, spacing, } from './scales.js';
function flattenRamps() {
    const out = {};
    for (const [name, ramp] of Object.entries(primitiveRamps)) {
        ramp.forEach((hex, shade) => {
            out[`color.primitive.${name}.${shade}`] = hex;
        });
    }
    return out;
}
function flattenSemantic(group, record) {
    const out = {};
    for (const [key, token] of Object.entries(record)) {
        out[`color.${group}.${key}`] = token.value;
    }
    return out;
}
function flattenNestedSemantic(group, record) {
    const out = {};
    for (const [outer, slots] of Object.entries(record)) {
        for (const [inner, token] of Object.entries(slots)) {
            out[`color.${group}.${outer}.${inner}`] = token.value;
        }
    }
    return out;
}
function flattenButtonColor(record) {
    const out = {};
    for (const [variant, intents] of Object.entries(record)) {
        for (const [intentName, slots] of Object.entries(intents)) {
            for (const [slot, token] of Object.entries(slots)) {
                out[`color.button.${variant}.${intentName}.${slot}`] = token.value;
            }
        }
    }
    return out;
}
function flattenScale(prefix, record) {
    const out = {};
    for (const [key, value] of Object.entries(record)) {
        out[`${prefix}.${key}`] = value;
    }
    return out;
}
function flattenHeadingSize() {
    const out = {};
    for (const [level, fields] of Object.entries(headingSize)) {
        for (const [field, value] of Object.entries(fields)) {
            out[`typography.headingSize.${level}.${field}`] = value;
        }
    }
    return out;
}
function flattenChartCategorical() {
    const out = {};
    for (const slot of chartCategorical) {
        out[`color.chart.categorical.${slot.name}.light`] = slot.light;
        out[`color.chart.categorical.${slot.name}.dark`] = slot.dark;
    }
    return out;
}
function flattenChartSequential() {
    const out = {};
    for (const [anchor, modes] of Object.entries(chartSequential)) {
        for (const mode of ['light', 'dark']) {
            modes[mode].forEach((hex, step) => {
                out[`color.chart.sequential.${anchor}.${mode}.${step}`] = hex;
            });
        }
    }
    return out;
}
/**
 * Every token as a flat `dotted.name → value` map. Iteration order is stable:
 * primitives, then semantic color, then chart scales, then non-color scales.
 * The flatten helpers preserve literal keys, so {@link FlatTokenName} is the
 * precise union of token names — a typo'd key is a compile error, and a later
 * W3C/Style-Dictionary export gets full key-completeness checking.
 */
export const flatTokens = Object.freeze({
    ...flattenRamps(),
    ...flattenScale('color.primitive', commonColors),
    ...flattenSemantic('intent', intent),
    ...flattenSemantic('surface', surface),
    ...flattenSemantic('text', text),
    ...flattenSemantic('border', border),
    ...flattenSemantic('onColor', onColor),
    ...flattenNestedSemantic('interactionState', interactionState),
    ...flattenNestedSemantic('intentTint', intentTint),
    ...flattenNestedSemantic('intentIndicator', intentIndicator),
    ...flattenButtonColor(buttonColor),
    ...flattenNestedSemantic('buttonFilledBackground', buttonFilledBackgroundState),
    ...flattenSemantic('buttonDisabled', buttonDisabled),
    ...flattenChartCategorical(),
    ...flattenChartSequential(),
    ...flattenScale('spacing', spacing),
    ...flattenScale('radius', radius),
    ...flattenScale('typography.fontFamily', fontFamily),
    ...flattenScale('typography.fontSize', fontSize),
    ...flattenScale('typography.lineHeight', lineHeight),
    ...flattenScale('typography.fontWeight', fontWeight),
    ...flattenHeadingSize(),
    ...flattenScale('elevation', elevation),
    ...flattenScale('layer', layer),
});
